import React, { useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { CircularProgress, ThemeProvider } from '@material-ui/core';
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react';
import { datadogRum } from '@datadog/browser-rum';

import { getTheme } from '@/utils/productHelpers';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { REDIRECT_TO_SESSION_KEY, CANOPY_SIGN_UP_CALLBACK, CANOPY_LOGIN_CALLBACK } from '@/utils/constants';
import { buildRedirectParam, isSafeRedirectUri } from '@/utils/urlHelpers';
import { logError } from '@/utils/logError';
import { ActiveFeatureFlag, isBooleanFeatureFlagEnabled } from '@/hooks/usePostHogFeatureFlags';

const getEntitlementStatus = (accessToken: string): string | undefined => {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    return payload.bh_claims?.products?.[0]?.status;
  } catch {
    return undefined;
  }
};

const SleepioEmailCallback: React.FC = () => {
  const history = useHistory();
  const { lob } = useParams<{ lob: string }>();
  const { search } = useLocation();
  const posthog = usePostHog();
  const supabaseAuthEnabled = isBooleanFeatureFlagEnabled(
    useFeatureFlagVariantKey(ActiveFeatureFlag.CAP_USE_SUPABASE_AUTH),
  );

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const redirectToParam = new URLSearchParams(search).get('redirectTo');
        if (redirectToParam && isSafeRedirectUri(redirectToParam)) {
          sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectToParam);
        }

        await new Promise((resolve) => { setTimeout(resolve, 1500); });

        const { data: { session } } = await getSupabaseClient().auth.getSession();

        if (!session) {
          history.replace('/activate/sleepio/access-code', { error: 'email_verification_failed' });
          return;
        }

        // canonicalPosthogDistinctId is the id VAF already has on file for this person
        // (their original signup's id, still property-carrying), not this session's own
        // fresh anonymous id. Falls back to phDistinctId for a brand-new signup, where
        // the two are the same. Uses alias(), not identify(): identify() only sends the
        // merge-triggering event on the FIRST identify() call of an anonymous session —
        // if anything (e.g. partner-id URL stitching) already identified this browser
        // this session, a later identify() to a different id silently sends nothing.
        // alias() always sends the merge event regardless of prior identified state.
        const canonicalPhDistinctId = session.user?.user_metadata?.canonicalPosthogDistinctId
          ?? session.user?.user_metadata?.phDistinctId;
        const currentDistinctId = posthog?.get_distinct_id();
        const willAlias = typeof canonicalPhDistinctId === 'string' && !!canonicalPhDistinctId
          && canonicalPhDistinctId !== currentDistinctId;

        // TEMP debug logging (CAN-1268) — remove once login stitching is confirmed working.
        datadogRum.addAction('[SleepioEmailCallback] posthog alias check', {
          hasCanonicalId: typeof session.user?.user_metadata?.canonicalPosthogDistinctId === 'string',
          hasPhDistinctId: typeof session.user?.user_metadata?.phDistinctId === 'string',
          canonicalPhDistinctId,
          currentDistinctId,
          willAlias,
        });

        if (willAlias) {
          posthog?.alias(canonicalPhDistinctId);
        }

        if (supabaseAuthEnabled === null) { return; }

        if (!supabaseAuthEnabled) {
          history.replace('/activate/sleepio/employers');
          return;
        }

        const entitlementStatus = getEntitlementStatus(session.access_token);
        const rawAccessCode = session.user?.user_metadata?.accessCode;
        const accessCode = typeof rawAccessCode === 'string' && rawAccessCode.trim()
          ? rawAccessCode.trim()
          : null;
        const isReturningUser = !accessCode && entitlementStatus === 'active';

        const orgSlug = new URLSearchParams(search).get('org_slug') ?? '';
        const intent = isReturningUser ? 'login' : 'sign-up';
        const storedRedirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);
        const fallback = isReturningUser ? CANOPY_LOGIN_CALLBACK : CANOPY_SIGN_UP_CALLBACK;
        const effectiveRedirectTo = (storedRedirectTo && isSafeRedirectUri(storedRedirectTo))
          ? storedRedirectTo
          : fallback;
        const redirectParam = buildRedirectParam(effectiveRedirectTo);
        history.replace(`/activate/sleepio-canopy/${lob}/${orgSlug}/${intent}/email/callback${redirectParam}`);
      } catch (err) {
        logError('[SleepioEmailCallback]', 'Unexpected error', err);
        history.replace('/activate/sleepio/access-code', { error: 'email_verification_failed' });
      }
    };

    handleCallback();
  }, [history, lob, search, supabaseAuthEnabled]);

  return (
    <ThemeProvider theme={getTheme('sleepio')}>
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      }}
      >
        <CircularProgress color="primary" />
      </div>
    </ThemeProvider>
  );
};

export default SleepioEmailCallback;
